import { Platform } from "react-native";
import type { SharedValue } from "react-native-reanimated";
import { useAnimatedStyle } from "react-native-reanimated";
import { StyleSheet } from "react-native-unistyles";

import useAnimatedTheme from "$src/core/hooks/use-animated-theme";
import CustomPlatform from "$src/core/utils/platform/platform";

const stylesheet = StyleSheet.create(({ font, colors, border, spacing }) => ({
  group: {
    variants: {
      groupDirection: {
        column: {
          flexDirection: "column",
        },
        row: {
          flexDirection: "row",
        },
      },
    },
  },
  pressable: {
    paddingHorizontal: spacing(5),
    gap: spacing(2),
    flexDirection: "row",
    alignItems: "center",
    borderWidth: border.widths.md,
    borderColor: "transparent",
    variants: {
      preset: {
        primary: {
          backgroundColor: colors.backgroundFocus,
          color: colors.foregroundOnInverted,
          borderColor: "transparent",
          _web: {
            stroke: colors.foregroundOnInverted,
          },
        },
        secondary: CustomPlatform.select({
          tv: {
            borderColor: colors.borderDefault,
            _web: {
              stroke: colors.foregroundDefault,
            },
          },
          default: {
            backgroundColor: colors.backgroundSurface,
            color: colors.foregroundDefault,
            borderColor: "transparent",
            _web: {
              stroke: colors.foregroundDefault,
            },
          },
        }),
        transparent: {
          backgroundColor: "transparent",
          color: colors.foregroundDefault,
          borderColor: colors.borderDefault,
          _web: {
            stroke: colors.foregroundDefault,
          },
        },
        link: {
          backgroundColor: "transparent",
          color: colors.foregroundDefault,
          borderColor: "transparent",
          padding: 0,
        },
      },
      size: {
        sm: {
          height: spacing(10),
        },
        md: {
          height: spacing(12),
        },
        lg: {
          height: spacing(16),
        },
      },
      align: {
        left: {
          justifyContent: "flex-start",
        },
        center: {
          justifyContent: "center",
        },
        right: {
          justifyContent: "flex-end",
        },
      },
      isDisabled: {
        true: {
          opacity: 0.4,
        },
      },
      isRounded: {
        true: {
          borderRadius: border.radius.full,
        },
        default: {
          borderRadius: border.radius.md,
        },
      },
      isSquared: {
        true: {
          aspectRatio: 1,
          paddingHorizontal: 0,
          paddingVertical: 0,
          _web: {
            width: "max-content",
          },
        },
      },
      isLoading: {
        true: {
          paddingHorizontal: 0,
        },
      },
      isInGroup: { true: {} },
      isLastInGroup: { true: {} },
      isFirstInGroup: { true: {} },
      groupDirection: { column: {}, row: {} },
    },
    compoundVariants: [
      {
        isInGroup: true,
        isFirstInGroup: true,
        isLastInGroup: false,
        styles: {
          borderBottomWidth: 0,
        },
      },
      {
        isInGroup: true,
        isFirstInGroup: false,
        isLastInGroup: false,
        styles: {
          borderBottomWidth: 0,
        },
      },
      {
        isInGroup: true,
        isFirstInGroup: false,
        isLastInGroup: false,
        styles: {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
      {
        isInGroup: true,
        isFirstInGroup: true,
        isLastInGroup: false,
        groupDirection: "row",
        styles: {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
      {
        isInGroup: true,
        isFirstInGroup: false,
        isLastInGroup: true,
        groupDirection: "row",
        styles: {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        },
      },
      {
        isInGroup: true,
        isFirstInGroup: true,
        isLastInGroup: false,
        groupDirection: "column",
        styles: {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
      {
        isInGroup: true,
        isFirstInGroup: false,
        isLastInGroup: true,
        groupDirection: "column",
        styles: {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
      },
    ],
    _web: {
      transition: "0.2s ease-in-out",
      "_active:not(:disabled)": {
        transform: "scale(0.98)",
      },
      "_hover:not(:disabled)": {
        opacity: 0.8,
      },
      _focus: {
        outline: "none",
      },
      "_focus-visible": {
        outlineStyle: "solid",
        outlineWidth: border.widths.md,
        outlineOffset: border.widths.md,
        outlineColor: colors.borderFocus,
        borderRadius: border.radius.md,
      },
    },
  },
  icon: {
    variants: {
      preset: {
        primary: {
          color: colors.foregroundOnInverted,
        },
        secondary: {
          color: colors.foregroundDefault,
        },
        transparent: {
          color: colors.foregroundDefault,
        },
        link: {
          color: colors.foregroundDefault,
        },
        default: {
          color: colors.foregroundDefault,
        },
      },
      size: {
        sm: {
          width: spacing(4),
          height: spacing(4),
        },
        md: {
          width: spacing(4.5),
          height: spacing(4.5),
        },
        lg: {
          width: spacing(6),
          height: spacing(6),
        },
      },
      isSquared: { true: {}, default: {} },
    },
    // Icon-only buttons (no label) get a slightly larger glyph.
    compoundVariants: [
      {
        isSquared: true,
        size: "sm",
        styles: { width: spacing(5), height: spacing(5) },
      },
      {
        isSquared: true,
        size: "md",
        styles: { width: spacing(5.5), height: spacing(5.5) },
      },
      {
        isSquared: true,
        size: "lg",
        styles: { width: spacing(7), height: spacing(7) },
      },
    ],
  },
  iconFocused: {
    variants: {
      preset: {
        primary: {
          color: colors.foregroundOnInverted,
        },
        secondary: {
          color: Platform.isTV
            ? colors.foregroundOnInverted
            : colors.foregroundDefault,
        },
        transparent: CustomPlatform.select({
          tv: {
            color: colors.foregroundOnInverted,
          },
          default: {
            color: colors.foregroundDefault,
          },
        }),
        link: {
          color: colors.foregroundDefault,
        },
        default: {
          color: colors.foregroundDefault,
        },
      },
    },
  },
  label: {
    flexShrink: 1,
    minWidth: 0,
    fontFamily: font.families.button,
    variants: {
      preset: {
        primary: {
          color: colors.foregroundOnInverted,
        },
        secondary: CustomPlatform.select({
          tv: {
            color: colors.foregroundOnInverted,
          },
          default: {
            color: colors.foregroundDefault,
          },
        }),
        transparent: {
          color: colors.foregroundDefault,
        },
        link: {
          color: colors.foregroundDefault,
        },
        default: {
          color: colors.foregroundDefault,
        },
      },
    },
  },
  labelFocused: {
    variants: {
      preset: {
        primary: {
          color: colors.foregroundOnInverted,
        },
        secondary: {
          color: Platform.isTV
            ? colors.foregroundOnInverted
            : colors.foregroundDefault,
        },
        transparent: CustomPlatform.select({
          tv: {
            color: colors.foregroundOnInverted,
          },
          default: {
            color: colors.foregroundDefault,
          },
        }),
        link: {
          color: colors.foregroundDefault,
        },
        default: {
          color: colors.foregroundDefault,
        },
      },
    },
  },
  loader: {
    justifyContent: "center",
    alignItems: "center",
    marginHorizontal: -border.widths.md,
  },
}));

export const useAnimatedStyles = ({
  isFocused,
}: {
  isFocused: SharedValue<boolean>;
}) => {
  const animatedTheme = useAnimatedTheme();

  const pressableStyle = useAnimatedStyle(() => {
    const { border, colors } = animatedTheme.value;
    const {
      backgroundFocus,
      foregroundOnInverted,
      foregroundDefault,
      borderFocus,
    } = colors;

    if (Platform.isTV) {
      return {
        backgroundColor: isFocused.value ? backgroundFocus : "transparent",
        color: isFocused.value ? foregroundOnInverted : foregroundDefault,
      };
    }

    // iOS only — on Android `outlineColor` hits an unchecked `as Int?` cast
    // that crashes release builds at mount (see `docs/gotchas.md`).
    if (Platform.OS === "ios") {
      return {
        outlineColor: isFocused.value ? borderFocus : "transparent",
        outlineWidth: border.widths.md,
        outlineOffset: border.widths.md,
        outlineStyle: "solid",
      };
    }

    return {};
  });

  return {
    pressableStyle,
  };
};

export default stylesheet;
